import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import type { JwtToken } from '@/api/generated/types'
import { env } from '@/env'
import { tokenStore } from '@/features/auth/tokens'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number | undefined,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function messageFromFieldMap(record: Record<string, unknown>): string | undefined {
  if (typeof record.message === 'string' && record.message) return record.message
  if (typeof record.detail === 'string' && record.detail) return record.detail
  const fields = Object.entries(record).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].length > 0,
  )
  if (fields.length === 0) return undefined
  if (fields.length === 1) {
    const only = fields[0]
    if (!only) return undefined
    return only[1]
  }
  return fields.map(([key, value]) => `${key}: ${value}`).join('; ')
}

function messageFromResponseData(data: unknown): string | undefined {
  if (data == null) return undefined
  if (typeof data === 'string') return data
  if (Array.isArray(data)) {
    const parts = data
      .map((item) => {
        if (typeof item === 'string') return item
        if (!item || typeof item !== 'object') return undefined
        return messageFromFieldMap(item as Record<string, unknown>)
      })
      .filter((part): part is string => Boolean(part))
    return parts.length ? parts.join('; ') : undefined
  }
  if (typeof data === 'object') return messageFromFieldMap(data as Record<string, unknown>)
  return undefined
}

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error
  if (axios.isAxiosError(error)) {
    const data = error.response?.data
    const fromBody = messageFromResponseData(data)
    const status = error.response?.status
    const fallback =
      status && status >= 400 && status < 500
        ? `Request failed with status code ${status}`
        : error.message
    return new ApiError(fromBody ?? fallback, status, data)
  }
  return new ApiError(error instanceof Error ? error.message : 'Unknown error', undefined)
}

export const authClient = axios.create({ baseURL: env.VITE_API_URL })

export const api = axios.create({ baseURL: env.VITE_API_URL })

let onAuthLost: () => void = () => {}
export function setAuthLostHandler(handler: () => void) {
  onAuthLost = handler
}

let refreshing: Promise<string> | null = null

export function refreshSession(): Promise<string> {
  refreshing ??= (async () => {
    const refreshToken = tokenStore.getRefreshToken()
    if (!refreshToken) throw new ApiError('No refresh token', 401)
    const { data } = await authClient.post<JwtToken>('/auth/refresh', {
      refresh_token: refreshToken,
    })
    if (!data.token) throw new ApiError('Refresh response missing token', 401)
    tokenStore.set({ token: data.token, refresh_token: data.refresh_token })
    return data.token
  })().finally(() => {
    refreshing = null
  })
  return refreshing
}

api.interceptors.request.use((config) => {
  const token = tokenStore.getToken()
  if (token) config.headers.set('Authorization', `Bearer ${token}`)
  return config
})

type Retryable = InternalAxiosRequestConfig & { _retried?: boolean }

api.interceptors.response.use(undefined, async (error: AxiosError) => {
  const config = error.config as Retryable | undefined
  if (error.response?.status !== 401 || !config || config._retried) {
    throw toApiError(error)
  }
  config._retried = true
  try {
    const token = await refreshSession()
    config.headers.set('Authorization', `Bearer ${token}`)
    return await api.request(config)
  } catch (refreshError) {
    tokenStore.clear()
    onAuthLost()
    throw toApiError(refreshError)
  }
})

export const loginRequest = (username: string, password: string) =>
  authClient
    .post<JwtToken>('/auth/login', { username, password })
    .then((r) => {
      if (!r.data.token) throw new ApiError('Login response missing token', 401)
      return { token: r.data.token, refresh_token: r.data.refresh_token }
    })
    .catch((e: unknown) => {
      throw toApiError(e)
    })
